import React from 'react'
import { Container, Card, Row, Col } from 'react-bootstrap'
import {
  formatCurrency,
  formatNumber,
  capitalizeEachFirstLetter,
  IsVehicleElectric,
} from '../../../helpers'
import { CardRowItem } from '../../cardRowItem'
import { CardRowItemHideCurrencyIfNoValue } from '../../cardRowItemHideCurrencyIfNoValue'
import { Icon } from 'icons'
import * as S from './proposalElements'
import OlyLink from 'components/olyLink'
import { BText, SmallText } from 'components/styledComponents/text'
import styled from '@emotion/styled'
import {
  IAfterMarketAccessoriesProps,
  IProposalNovatedLeaseSectionProps,
} from './proposalTypes'
import SpartaCustomerCount from '../estimate-compare/spartaCustomerCount'

interface IInputProps {
  title: string
  afterMarketAccessories: IAfterMarketAccessoriesProps
  isProposal: boolean | undefined
  data: IProposalNovatedLeaseSectionProps
}

const novatedLeaseSection = (props: IInputProps) => {
  const extendedWarrantyTerm = props.data.extendedWarrantyTerm
  const getAddOnTerm = () => {
    return extendedWarrantyTerm && extendedWarrantyTerm > 0
      ? extendedWarrantyTerm > 1
        ? '- ' + extendedWarrantyTerm + ' years'
        : '- ' + extendedWarrantyTerm + ' year'
      : ''
  }
  const pacVal = 'pac'
  const rpcProviderlower =
    props.data.rpcProvider && props.data.rpcProvider.toLowerCase()
  const extendedWarrantyProviderlower =
    props.data.extendedWarrantyProvider &&
    props.data.extendedWarrantyProvider.toLowerCase()
  const isPacInsurer =
    rpcProviderlower === pacVal || extendedWarrantyProviderlower === pacVal
  const totalLossAssist = isPacInsurer
    ? 'Total Loss Assist Insurance'
    : 'Total Loss Assist'
  const safeGuardorExtended = isPacInsurer
    ? `Extended Warranty Insurance  ${getAddOnTerm()}`
    : 'Safeguard Ultimate Warranty'
  const extendedOneLiner = isPacInsurer
    ? 'Cover for unexpected mechanical or electrical failure to most components.'
    : `Extend the car’s factory warranty.`
  const rpcText = isPacInsurer
    ? 'Guaranteed Buy Back  Insurance'
    : 'Residual Protection Cover'
  const rpcOneLiner = isPacInsurer
    ? 'Cover in most scenarios if the market value of your car is less than the residual amount at the end of your full lease term.'
    : 'Cover in case the car’s market value is less than the residual.'
  const learnMoreURL = 'https://oly.com.au/novated-lease/insurance/'


  let annualKmsText = formatNumber(props.data.kmPerAnnum) + ' Kms'
  let leaseProtectionPremium =
    props.data.leaseProtectionPremium > 0
      ? formatCurrency(props.data.leaseProtectionPremium)
      : 'Not included'
  let extendedWarrantyPremium =
    props.data.extendedWarrantyPremium > 0
      ? formatCurrency(props.data.extendedWarrantyPremium)
      : 'Not included'
  let rpcPremium =
    props.data.rpcPremium > 0
      ? formatCurrency(props.data.rpcPremium)
      : 'Not included'
  let gapCoverPremium =
    props.data.gapCoverPremium > 0
      ? formatCurrency(props.data.gapCoverPremium)
      : 'Not included'

  const tickCross = (val: number) =>
    val > 0 ? (
      <Icon iconType="GreenTick" size={1} />
    ) : (
      <Icon iconType="OlyCross" size={1} />
    )

  let procurementFee =
    props.data.vehicle && props.data.vehicle.procurementFee > 0
      ? `Includes procurement fee of ${formatCurrency(
          props.data.vehicle.procurementFee
        )} ex GST`
      : null

  const getVehicleType = (props: IInputProps) => {
    let retval = ''
    if (props.data && props.data.vehicle && props.data.vehicle.vehicleType)
      retval = props.data.vehicle.vehicleType.toLowerCase()
    return retval
  }

  let vehicleType = getVehicleType(props)

  let isEVRebate =
    props.data.evRebateApplicable && IsVehicleElectric(props.data, false) && vehicleType.includes('new')

  let getLeaseType = () => {
    let vehicleType = getVehicleType(props)
    if (props.data.vehicle && props.data.vehicle.id) {
      if (vehicleType.includes('new')) {
        return 'New Car Lease'
      } else if (vehicleType.includes('residual')) {
        return 'Refinance Existing Lease'
      } else if (vehicleType.includes('leaseback')) {
        return 'Lifestyle Lease'
      } else if (vehicleType.includes('private')) {
        return 'Private Purchase Sale'
      } else {
        return 'Used Car Lease'
      }
    } else {
      if (vehicleType.includes('new')) {
        return 'New Car Lease'
      } else if (vehicleType.includes('residual')) {
        return 'Refinance Existing Lease'
      } else if (vehicleType.includes('leaseback')) {
        return 'Lifestyle Lease'
      } else if (vehicleType.includes('private')) {
        return 'Private Purchase Sale'
      } else {
        return 'Used Car Lease'
      }
    }
  }

  const aftermarketOrderLines =
    props.afterMarketAccessories &&
    props.afterMarketAccessories.aftermarketOrderLines
  let aftermarketTotal = 0
  aftermarketOrderLines &&
    aftermarketOrderLines.forEach(
      (item: { description: string; priceIncGST: number }) =>
        (aftermarketTotal += item.priceIncGST)
    )

  const accessoriesByYou =
    props.data.vehicle &&
    props.data.vehicle.thirdPartyOption &&
    props.data.vehicle.thirdPartyOption.thirdPartyOptionLines

  const vehicleAccessories =
    props.data.vehicle && props.data.vehicle.vehicleAccessories

  let accessoriesDesc = ''
  vehicleAccessories &&
    vehicleAccessories.forEach(
      (item: { name: string; amount: number }) =>
        (accessoriesDesc +=
          item.name + ' at ' + formatCurrency(item.amount) + ', ')
    )
  let icon = <Icon iconType="EVBolt" />
  let line1 = `This electric vehicle may be eligible for a ${formatCurrency(props.data.evRebateValue)} EV Bonus!`
  let line2 = `The ${formatCurrency(props.data.evRebateValue)} EV Bonus is not included in any calculations below. ` +
                ('<a href=' + props.data.evRebateLink +
                  ' target="_blank" rel="noopener noreferrer" class="link">' +
                  'Terms &amp; Conditions Apply' +
                  '</a>')


  let compInsuranceSubtext = props.data.compInsuranceProvider !== 'Allianz'
    ? '(Oly arranged)'
    : '(Oly arranged, you have the choice of repairer. Please refer to your PDS for details about choice of repairer.)'

  return (
    <div
      className="card-section  print-page-break-before"
      id="novatedleasesection"
    >
      <Container className="section mt-4">
        <Card
          className={
            props.data.pdf
              ? 'mb-13rem pdf-card-container'
              : 'mb-13rem card-shadow'
          }
        >
          <Card.Header
            className={
              props.data.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
            }
          >
            <div className="text-header">{props.title}</div>
          </Card.Header>
          <Card.Body>
          {isEVRebate && props.data.pdf && (
            <SpartaCustomerCount
              line1={ line1 }
              line2={ line2 }
              extended={false}
              icon={icon}
              iconTopLine={true}
              spacingValue={1}
            />
          )}
            <CardRowItem
              title="Lease term (months)"
              value={props.data.leaseTerm}
            />
            <CardRowItem title="Chosen lease type" value={getLeaseType()} />
            <CardRowItem
              title="Chosen lease product type"
              value={capitalizeEachFirstLetter(
                props.data.productId
                  .toLowerCase()
                  .replace('novated lease', '')
                  .trim()
              )}
              id="novatedleasesection.leasetype"
            />
            <CardRowItem
              title="Residual value (inc GST)"
              value={props.data.leaseResidualAmountIncGST}
              isCurrency="true"
            />
            <CardRowItem
              title="Estimated annual kilometers"
              value={annualKmsText}
            />
            {/* //Not sure if this is wanted or not.
            <CardRowItem
              title="Vehicle FBT Base value"
              value={props.data.vehicleFBTValue}
              isCurrency="true"
            /> */}
                 <hr></hr> 

            <div className="mt-2">
              <div className="font-weight-bold text-uppercase">
                Total amount financed
              </div>
              <CardRowItem
                title="Vehicle purchase price"
                value={
                  props.data.vehicle && props.data.vehicle.invoiceTotal
                    ? props.data.vehicle.invoiceTotal
                    : props.data.driveawayPrice
                }
                isCurrency="true"
                rightTextcss="text-right--primary text-right--bold"
                subtitle2={procurementFee}
                isSubtitle2CcyLarge="true"
              />
              {props.isProposal && (
                <SmallText>
                  {accessoriesDesc == ''
                    ? '(Based on on roads, GST on vehicle and accessories if applicable.)'
                    : "(Based on dealer's accessories including " +
                      accessoriesDesc +
                      'on roads, & GST on vehicle and accessories if applicable.)'}
                </SmallText>
              )}

              {((aftermarketOrderLines && aftermarketOrderLines.length > 0) ||
                (vehicleAccessories &&
                  vehicleAccessories.length > 0 &&
                  !props.isProposal) ||
                (accessoriesByYou && accessoriesByYou.length > 0)) && (
                <div>
                  <hr></hr>
                  <CardRowItem title="ACCESSORIES" />
                  {!props.isProposal &&
                    vehicleAccessories &&
                    vehicleAccessories.length > 0 && (
                      <div>
                        <CardRowItemHideCurrencyIfNoValue
                          title="Arranged by Dealership"
                          value={null}
                          rightTextcss="text-right--bold"
                        />
                        <div className="ml-3">
                          {vehicleAccessories.map((item) => (
                            <CardRowItemHideCurrencyIfNoValue
                              subtitle2={item.name}
                              value={item.amount}
                              isCurrency="true"
                              rightTextcss="text-right"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  {aftermarketOrderLines &&
                    aftermarketOrderLines.length > 0 && (
                      <div>
                        <CardRowItemHideCurrencyIfNoValue
                          title="Arranged by Oly"
                          value={null}
                          mediumWeight
                        />
                        <div className="ml-3">
                          {aftermarketOrderLines.map(
                            (item: any, index: number) => (
                              <CardRowItemHideCurrencyIfNoValue
                                key={item.description + index}
                                subtitle2={item.description}
                                value={item.priceIncGST}
                                isCurrency="true"
                                rightTextcss="text-right"
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {accessoriesByYou && accessoriesByYou.length > 0 && (
                    <CardRowItemHideCurrencyIfNoValue
                      title="Arranged by you"
                      value={null}
                      mediumWeight
                    />
                  )}
                  <div className="ml-3">
                    {accessoriesByYou &&
                      accessoriesByYou.map((item, index) => (
                        <CardRowItemHideCurrencyIfNoValue
                          key={item.description + index}
                          subtitle2={item.description}
                          value={item.amountIncGST}
                          isCurrency="true"
                          rightTextcss="text-right"
                        />
                      ))}
                  </div>
                  <hr></hr>
                </div>
              )}
              {props.data.gstCreditClaimable > 0 && (
                <CardRowItem
                  title="Less GST savings on vehicle"
                  value={props.data.gstCreditClaimable * -1}
                  isCurrency="true"
                  rightTextcss="text-positive"
                />
              )}
              <CardRowItem
                title="Stamp duty via disbursement"
                value={
                  props.data.vehicle &&
                  props.data.vehicle.stampDutyViaDisbursement
                }
                isCurrency="true"
                hideIfNoValue="true"
              />
              <CardRowItem
                title="Negative equity"
                value={props.data.negativeEquityAmount}
                isCurrency="true"
                hideIfNoValue="true"
              />
              <CardRowItem
                title="Finance Establishment Fee"
                value={props.data.financierEstablishmentFee}
                isCurrency="true"
                hideIfNoValue="true"
              />
              {props.data.compInsurancePremiumFinanced && (
                <div>
                  <CardRowItem
                    title="1st year Comprehensive Insurance"
                    value={props.data.compInsurancePremium}
                    isCurrency="true"
                    hideIfNoValue="true"
                  />
                  <SmallText>
                    {compInsuranceSubtext}
                  </SmallText>
                </div>
              )}
              

              {props.data.downloadingPdf && (
                <div>
                  <hr></hr>
                  <ToBeContinuedBlock>
                    <BText>Continued on next page</BText>
                  </ToBeContinuedBlock>
                </div>
              )}
              {!props.data.downloadingPdf && (
                <div>
                  <div className="mt-2 mb-2">
                    <S.NovatedLeaseSection>
                      <>
                        {tickCross(props.data.leaseProtectionPremium)}
                        <div>
                          <CardRowItem
                            title="Lease Protection Insurance"
                            value={leaseProtectionPremium}
                          />
                          <div className="text-small">
                            Cover your lease repayments if you become
                            involuntarily unemployed.
                          </div>
                          <div className="mb-3">
                            <OlyLink
                              linkUrl={learnMoreURL + 'lease-protection'}
                              text="Learn more"
                              classes="text-underline"
                            />
                          </div>
                        </div>
                      </>
                    </S.NovatedLeaseSection>
                    <S.NovatedLeaseSection>
                      <>
                        {tickCross(props.data.rpcPremium)}
                        <div>
                          <CardRowItem
                            title={rpcText}
                            value={rpcPremium}
                          />
                          <div className="text-small">{rpcOneLiner}</div>
                          <div className="mb-3">
                            <OlyLink
                              linkUrl={learnMoreURL + 'guaranteed-buy-back'}
                              text="Learn more"
                              classes="text-underline"
                            />
                          </div>
                        </div>
                      </>
                    </S.NovatedLeaseSection>
                    {props.data.extendedWarrantyPremium > 0 &&
                    <S.NovatedLeaseSection>
                      <>
                        {tickCross(props.data.extendedWarrantyPremium)}
                        <div>
                          <CardRowItem
                            title={safeGuardorExtended}
                            value={extendedWarrantyPremium}
                          />
                          <div className="text-small">{extendedOneLiner}</div>
                          <div className="mb-3">
                            <OlyLink
                              linkUrl={learnMoreURL + 'extended-warranty'}
                              text="Learn more"
                              classes="text-underline"
                            />
                          </div>
                        </div>
                      </>
                    </S.NovatedLeaseSection>
                    }
                    <S.NovatedLeaseSection>
                      <>
                        {tickCross(props.data.gapCoverPremium)}
                        <div>
                          <CardRowItem
                            title={totalLossAssist}
                            value={gapCoverPremium}
                          />
                          <div className="text-small">
                            Cover in addition to your Comprehensive MV insurance
                            payout if your car is written off.
                          </div>
                          <div className="mb-3">
                            <OlyLink
                              linkUrl={learnMoreURL + 'total-loss-assist'}
                              text="Learn more"
                              classes="text-underline"
                            />
                          </div>
                        </div>
                      </>
                    </S.NovatedLeaseSection>
                  </div>
                  <hr></hr>
                  <Row>
                    <Col lg={2} md={0} xs={0}></Col>
                    <Col lg={10} md={12} xs={12}>
                      <CardRowItem
                        title="Total amount financed"
                        value={props.data.financeAmount}
                        isCurrency="true"
                        rightTextcss="text-right--bold"
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Container>
      {props.data.downloadingPdf && (
        <Container className="section mt-4">
          <Card
            className={
              props.data.pdf ? 'mb-13rem pdf-card-container' : 'mb-13rem'
            }
          >
            <Card.Header
              className={
                props.data.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
              }
            >
              <div className="text-header">{props.title}</div>
            </Card.Header>
            <Card.Body>
              <div>
                <div className="mt-2 mb-2">
                  <S.NovatedLeaseSection>
                    <>
                      {tickCross(props.data.leaseProtectionPremium)}
                      <div>
                        <CardRowItem
                          title="Lease Protection Insurance"
                          value={leaseProtectionPremium}
                          rightTextcss="text-right--bold"
                        />
                        <div className="text-small">
                          Cover your lease repayments if you become
                          involuntarily unemployed.
                        </div>
                        <div className="mb-3">
                          <OlyLink
                            linkUrl={learnMoreURL + 'lease-protection'}
                            text="Learn more"
                            classes="text-underline"
                          />
                        </div>
                      </div>
                    </>
                  </S.NovatedLeaseSection>
                  <S.NovatedLeaseSection>
                    <>
                      {tickCross(props.data.rpcPremium)}
                      <div>
                        <CardRowItem
                          title={rpcText}
                          value={rpcPremium}
                          rightTextcss="text-right--bold"
                        />
                        <div className="text-small">{rpcOneLiner}</div>
                        <div className="mb-3">
                          <OlyLink
                            linkUrl={learnMoreURL + 'guaranteed-buy-back'}
                            text="Learn more"
                            classes="text-underline"
                          />
                        </div>
                      </div>
                    </>
                  </S.NovatedLeaseSection>
                  <S.NovatedLeaseSection>
                    <>
                      {tickCross(props.data.extendedWarrantyPremium)}
                      <div>
                        <CardRowItem
                          title={safeGuardorExtended}
                          value={extendedWarrantyPremium}
                          rightTextcss="text-right--bold"
                        />
                        <div className="text-small">{extendedOneLiner}</div>
                        <div className="mb-3">
                          <OlyLink
                            linkUrl={learnMoreURL + 'extended-warranty'}
                            text="Learn more"
                            classes="text-underline"
                          />
                        </div>
                      </div>
                    </>
                  </S.NovatedLeaseSection>
                  <S.NovatedLeaseSection>
                    <>
                      {tickCross(props.data.gapCoverPremium)}
                      <div>
                        <CardRowItem
                          title={totalLossAssist}
                          value={gapCoverPremium}
                          rightTextcss="text-right--bold"
                        />
                        <div className="text-small">
                          Cover in addition to your Comprehensive MV insurance
                          payout if your car is written off.
                        </div>
                        <div className="mb-3">
                          <OlyLink
                            linkUrl={learnMoreURL + 'total-loss-assist'}
                            text="Learn more"
                            classes="text-underline"
                          />
                        </div>
                      </div>
                    </>
                  </S.NovatedLeaseSection>
                </div>
                <hr></hr>
                <Row>
                  <Col lg={2} md={0} xs={0}></Col>
                  <Col lg={10} md={12} xs={12}>
                    <CardRowItem
                      title="Total amount financed"
                      value={props.data.financeAmount}
                      isCurrency="true"
                      rightTextcss="text-right--bold"
                    />
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Container>
      )}
    </div>
  )
}

export default novatedLeaseSection

// styled components

const ToBeContinuedBlock = styled('div')`
  label: ToBeContinuedBlock;
  display: flex;
  flex-direction: row-reverse;
`
